import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Package, Microscope, Truck, ArrowRight, ArrowUpRight } from "lucide-react";
import { fetchProductsLite, fetchSiteConfig } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { RecentlyViewedRail } from "@/components/RecentlyViewedRail";
import { TrustpilotWidget } from "@/components/TrustpilotWidget";

const trustBadges = [
    { icon: ShieldCheck, label: "JANOSHIK TESTED", sub: "Third-Party COA" },
    { icon: Package, label: "UNMARKED PARCELS", sub: "Plain · No Labels" },
    { icon: Truck, label: "FAST CA → US/CA", sub: "Canadian Warehouse" },
    { icon: Microscope, label: "≥99% PURITY", sub: "Research Grade" },
];

const FALLBACK_HERO = {
    eyebrow: "/ 03 · OUR BRANDING",
    title_line_1: "Lab-grade vials.",
    title_accent: "Premium",
    title_line_2: " craft.",
    body: "Every Helixora vial ships with premium matte-black labeling and uncompromising quality in both presentation and peptide purity. We take immense pride in every product we deliver.",
    stat_tiles: [
        { k: "48H", v: "PROCESSING WINDOW" },
        { k: "100%", v: "TRACKED PARCELS" },
        { k: "★", v: "SIGNATURE BRANDED VIALS" },
    ],
    image_url: "https://customer-assets.emergentagent.com/job_sterile-science/artifacts/o3hno6g2_image.png",
    image_caption_overline: "HELIXORA · BATCH 2026-Q2",
    image_caption: "Branded · lab-verified · ready to ship",
};

export default function Home() {
    // Seed from localStorage so the Home page paints featured + all grids INSTANTLY on revisit.
    // Fresh data from /api/products/lite arrives in ~100ms and overrides the cached render.
    const readCache = (key) => {
        try {
            const c = JSON.parse(localStorage.getItem(key) || "null");
            if (c && Array.isArray(c.products) && c.ts && (Date.now() - c.ts) < 24 * 60 * 60 * 1000) {
                return c.products;
            }
        } catch { /* ignore */ }
        return [];
    };
    const [featured, setFeatured] = useState(() => readCache("hxr_home_featured_v1"));
    const [all, setAll] = useState(() => readCache("hxr_products_cache_v1"));
    const [hero, setHero] = useState(FALLBACK_HERO);

    useEffect(() => {
        fetchSiteConfig().then((cfg) => {
            if (cfg?.home_hero) setHero({ ...FALLBACK_HERO, ...cfg.home_hero });
        }).catch(() => {});
    }, []);

    useEffect(() => {
        let attempts = 0;
        const loadFeatured = () => {
            attempts += 1;
            fetchProductsLite({ featured: true }).then((ps) => {
                const seen = new Set();
                const uniq = [];
                for (const p of ps) {
                    const key = p.variant_group || p.id;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    uniq.push(p);
                }
                setFeatured(uniq);
                try { localStorage.setItem("hxr_home_featured_v1", JSON.stringify({ products: uniq, ts: Date.now() })); } catch { /* ignore */ }
            }).catch(() => {
                // Network/cache failure (e.g. ERR_CACHE_WRITE_FAILURE on Chrome).
                // Retry up to 3 times with exponential backoff so a stale page recovers automatically.
                if (attempts < 3) setTimeout(loadFeatured, attempts * 800);
            });
        };
        let allAttempts = 0;
        const loadAll = () => {
            allAttempts += 1;
            fetchProductsLite().then((ps) => {
                const seen = new Set();
                const uniq = [];
                for (const p of ps) {
                    const key = p.variant_group || p.id;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    uniq.push(p);
                }
                setAll(uniq);
                try { localStorage.setItem("hxr_products_cache_v1", JSON.stringify({ products: uniq, ts: Date.now() })); } catch { /* ignore */ }
            }).catch(() => {
                if (allAttempts < 3) setTimeout(loadAll, allAttempts * 800);
            });
        };
        loadFeatured();
        loadAll();
    }, []);

    return (
        <div data-testid="home-page" className="bg-ink-base text-ink">
            {/* HERO */}
            <section className="relative overflow-hidden grain-overlay" data-testid="hero">
                <div
                    className="absolute inset-0 opacity-50"
                    style={{
                        backgroundImage: "url('https://images.pexels.com/photos/11363271/pexels-photo-11363271.jpeg?auto=compress&cs=tinysrgb&w=1600')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 hero-vignette" />
                <div className="absolute inset-0 grid-lines opacity-50" />

                <div className="relative max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-20 sm:py-28 lg:py-40">
                    <div className="grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-8">
                            <div className="flex items-center gap-4 mb-10 animate-fade-up">
                                <span className="h-px w-16 bg-neon" />
                                <p className="overline text-neon">HXR/001 · RESEARCH PEPTIDES · EST. 2022</p>
                            </div>
                            <h1 className="font-display text-5xl sm:text-7xl lg:text-[112px] font-bold leading-[0.92] tracking-tight stagger-1 animate-fade-up">
                                Peptides<br />
                                <span className="text-neon">Engineered</span><br />
                                for Precision.
                            </h1>
                            <p className="mt-10 text-base sm:text-lg text-ink-dim max-w-xl leading-relaxed stagger-2 animate-fade-up">
                                A sovereign research compound lab shipping pharmaceutical-grade peptides in plain, unmarked parcels from our North American & European warehouses. Every batch independently verified by domestic lab tests AND Janoshik analytical.
                            </p>
                            <div className="mt-12 flex flex-col sm:flex-row gap-3 stagger-3 animate-fade-up">
                                <Link
                                    to="/shop"
                                    className="group bg-neon hover:bg-neon-glow text-black px-8 py-4 font-bold tracking-wide inline-flex items-center justify-center gap-2 transition-colors"
                                    data-testid="hero-shop-cta"
                                >
                                    ENTER CATALOG <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                </Link>
                                <Link
                                    to="/shop?cat=GLP-1s"
                                    className="border border-ink-strong hover:border-neon text-ink px-8 py-4 font-bold tracking-wide inline-flex items-center justify-center transition-colors overline text-[11px]"
                                    data-testid="hero-glp-cta"
                                >
                                    GLP-1 STACK →
                                </Link>
                            </div>
                        </div>

                        {/* Right data panel */}
                        <div className="lg:col-span-4 lg:border-l border-ink-strong lg:pl-8 stagger-4 animate-fade-up">
                            <p className="overline text-ink-mute mb-6">SESSION // LIVE</p>
                            <dl className="space-y-4 font-mono text-sm">
                                {[
                                    { k: "PURITY FLOOR", v: "99.2%" },
                                    { k: "ASSAY METHOD", v: "HPLC + MS" },
                                    { k: "LEAD TIME", v: "24 to 72H" },
                                    { k: "SHIPPING", v: "UNMARKED" },
                                    { k: "PAYMENT", v: "CRYPTO" },
                                ].map((x) => (
                                    <div key={x.k} className="flex items-baseline justify-between gap-4 border-b border-ink pb-3">
                                        <dt className="text-ink-mute text-[11px] tracking-[0.15em]">{x.k}</dt>
                                        <dd className="text-ink font-semibold">{x.v}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Trust bar */}
                <div className="relative border-t border-ink bg-ink-base/80 backdrop-blur">
                    <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-5 grid grid-cols-2 md:grid-cols-4">
                        {trustBadges.map((b, i) => (
                            <div key={b.label} className={`flex items-center gap-3 py-2 ${i > 0 ? "md:border-l border-ink md:pl-6" : ""}`} data-testid={`trust-${b.label.replace(/\s+/g,'-').toLowerCase()}`}>
                                <b.icon className="w-4 h-4 text-neon shrink-0" strokeWidth={1.5} />
                                <div className="leading-tight">
                                    <div className="text-xs font-mono font-semibold text-ink tracking-wider">{b.label}</div>
                                    <div className="text-[10px] text-ink-mute mt-0.5">{b.sub}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Trustpilot micro-combo — sits inline with the trust bar so social proof lives beside the internal badges */}
                    <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 pb-4 flex items-center gap-3 border-t border-ink/60" data-testid="home-trustpilot">
                        <span className="overline text-[10px] tracking-[0.2em] text-ink-mute">/ REVIEWS</span>
                        <TrustpilotWidget variant="micro-combo" theme="dark" height="22px" className="flex-1" />
                    </div>
                </div>
            </section>

            {/* AFFORDABILITY MISSION */}
            <section className="relative py-24 sm:py-32 border-t border-ink bg-ink-base overflow-hidden" data-testid="affordability-section">
                <div className="absolute inset-0 grid-lines opacity-20" />
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-gold/5 blur-3xl pointer-events-none" />
                <div className="relative max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="h-px w-16 bg-gold" />
                            <p className="overline text-gold">/ MISSION · 01</p>
                        </div>
                        <h2 className="font-display text-5xl sm:text-7xl lg:text-[88px] font-bold text-ink tracking-tight leading-[0.92]">
                            <span className="text-gold">Affordability.</span>
                        </h2>
                        <p className="mt-8 text-lg sm:text-xl text-ink-dim leading-relaxed max-w-2xl">
                            Our goal is to make peptides <span className="text-ink font-semibold">affordable for labs to study</span> the ever-expanding world of peptides, and their use cases for treating various conditions and enhancement.
                        </p>
                        <p className="mt-6 text-sm text-ink-mute leading-relaxed max-w-2xl">
                            Research shouldn't be gated by markup. We price every compound to keep the barrier to entry as low as possible, no inflated retail layers, no MLM resellers, no hidden lab fees. Just pharmaceutical-grade peptides at ground-floor prices.
                        </p>
                    </div>
                    <div className="md:col-span-5">
                        <div className="grid grid-cols-2 gap-px bg-ink-strong">
                            {[
                                { k: "−40%", v: "VS RETAIL PEPTIDE STORES" },
                                { k: "0", v: "MIDDLE-MAN MARKUP" },
                                { k: "$0", v: "RESEARCHER ACCESS FEE" },
                                { k: "BULK", v: "PRICING ON KITS + TIERS" },
                            ].map((x) => (
                                <div key={x.v} className="bg-ink-base p-6">
                                    <div className="font-display text-3xl sm:text-4xl font-bold text-gold">{x.k}</div>
                                    <div className="overline text-[10px] text-ink-mute mt-2 leading-tight">{x.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* NEW ARRIVALS, shows products flagged is_new_arrival, hidden if none */}
            {(() => {
                const arrivals = all.filter((p) => p.is_new_arrival).slice(0, 4);
                if (arrivals.length === 0) return null;
                return (
                    <section className="py-20 border-t border-ink bg-ink-surface" data-testid="new-arrivals-section">
                        <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12">
                            <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
                                <div className="md:col-span-8">
                                    <p className="section-num mb-4 text-neon">/ NEW ARRIVALS · JUST DROPPED</p>
                                    <h2 className="font-display text-4xl sm:text-6xl font-bold text-ink tracking-tight leading-[0.95]">Fresh<br /><span className="text-neon">research stock.</span></h2>
                                </div>
                                <div className="md:col-span-4 flex md:justify-end">
                                    <Link to="/shop" className="overline text-neon link-reveal inline-flex items-center gap-2" data-testid="new-arrivals-view-all">
                                        VIEW FULL CATALOG <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="new-arrivals-grid">
                                {arrivals.map((p, idx) => <ProductCard key={p.id} product={p} index={idx} />)}
                            </div>
                        </div>
                    </section>
                );
            })()}

            {/* FEATURED */}
            <section className="py-24 sm:py-32 bg-ink-base" data-testid="featured-section">
                <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12">
                    <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
                        <div className="md:col-span-8">
                            <p className="section-num mb-4">/ 02 · MOST RESEARCHED</p>
                            <h2 className="font-display text-4xl sm:text-6xl font-bold text-ink tracking-tight leading-[0.95]">Featured<br /><span className="text-neon">compounds.</span></h2>
                        </div>
                        <div className="md:col-span-4 flex md:justify-end">
                            <Link to="/shop" className="overline text-neon link-reveal inline-flex items-center gap-2" data-testid="featured-view-all">
                                VIEW FULL CATALOG <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="featured-grid">
                        {featured.map((p, idx) => <ProductCard key={p.id} product={p} index={idx} />)}
                    </div>
                </div>
            </section>

            <RecentlyViewedRail />

            {/* DISCREET SHIPPING / EDITABLE HERO */}
            <section className="relative py-32 border-y border-ink overflow-hidden" data-testid="shipping-section">
                <div className="absolute inset-0 grid-lines opacity-40" />
                <div className="relative max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6">
                        <p className="section-num mb-4">{hero.eyebrow}</p>
                        <h2 className="font-display text-4xl sm:text-6xl font-bold text-ink tracking-tight leading-[0.95]">
                            {hero.title_line_1}<br />
                            {hero.title_accent && <span className="text-gold">{hero.title_accent}</span>}
                            {hero.title_line_2}
                        </h2>
                        {hero.body && (
                            <p className="mt-8 text-ink-dim leading-relaxed text-base max-w-lg whitespace-pre-line">
                                {hero.body}
                            </p>
                        )}
                        {hero.stat_tiles && hero.stat_tiles.length > 0 && (
                            <div className={`mt-10 grid gap-px bg-ink-strong max-w-lg grid-cols-${Math.min(hero.stat_tiles.length, 3)}`} style={{ gridTemplateColumns: `repeat(${Math.min(hero.stat_tiles.length, 3)}, minmax(0, 1fr))` }}>
                                {hero.stat_tiles.slice(0, 6).map((x, i) => (
                                    <div key={i} className="bg-ink-base p-6">
                                        <div className="font-display text-4xl font-bold text-neon">{x.k}</div>
                                        <div className="overline text-[10px] text-ink-mute mt-2">{x.v}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="md:col-span-6 relative">
                        <div className="relative aspect-[4/5] max-w-md mx-auto w-full border border-ink-strong">
                            {hero.image_url && (
                                <img
                                    src={hero.image_url}
                                    alt={hero.image_caption || "Home hero image"}
                                    className="w-full h-full object-cover"
                                />
                            )}
                            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-ink-base via-ink-base/60 to-transparent">
                                {hero.image_caption_overline && <p className="overline text-gold">{hero.image_caption_overline}</p>}
                                {hero.image_caption && <p className="font-display text-lg font-bold text-ink mt-1">{hero.image_caption}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="py-32" data-testid="categories-section">
                <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12">
                    <div className="mb-16">
                        <p className="section-num mb-4">/ 04 · CATALOG INDEX</p>
                        <h2 className="font-display text-4xl sm:text-6xl font-bold text-ink tracking-tight leading-[0.95]">Four research<br /><span className="text-neon">families.</span></h2>
                    </div>
                    <div className="grid md:grid-cols-4 gap-px bg-ink-strong">
                        {[
                            { cat: "GLP-1s", desc: "Metabolic research" },
                            { cat: "Healing Peptides", desc: "Regenerative repair" },
                            { cat: "HGH & Ancillaries", desc: "Hormonal axis" },
                            { cat: "Nootropics", desc: "Cognition & neuro-research" },
                        ].map((c, idx) => (
                            <Link
                                key={c.cat}
                                to={`/shop?cat=${encodeURIComponent(c.cat)}`}
                                className="group bg-ink-base hover:bg-ink-surface p-8 transition-colors relative"
                                data-testid={`category-tile-${idx}`}
                            >
                                <div className="overline text-neon mb-4">FAM · {String(idx + 1).padStart(2, "0")}</div>
                                <h3 className="font-display text-2xl font-bold text-ink group-hover:text-neon transition-colors leading-tight">{c.cat}</h3>
                                <p className="mt-3 text-xs text-ink-mute">{c.desc}</p>
                                <div className="mt-12 flex items-center justify-between">
                                    <span className="overline text-[10px] text-ink-mute">BROWSE</span>
                                    <ArrowUpRight className="w-4 h-4 text-ink-dim group-hover:text-neon group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CRYPTO / PAYMENT */}
            <section className="py-24 border-t border-ink bg-ink-surface" data-testid="payment-section">
                <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-7">
                        <p className="section-num mb-4">/ 05 · PAYMENT</p>
                        <h2 className="font-display text-3xl sm:text-5xl font-bold text-ink tracking-tight leading-[1.0]">Crypto checkout. <span className="text-gold">Beginner-friendly.</span></h2>
                        <p className="mt-6 text-ink-dim text-base max-w-xl leading-relaxed">
                            Guest checkout, no account required. Pay in <strong className="text-ink">USDC / USDT / BTC / ETH</strong> and 50+ other coins through our secure NowPayments processor. Prices stay in USD, the network converts at the live exchange rate. <Link to="/how-to-pay-with-crypto" className="text-neon hover:underline">New to crypto? Read the 3-minute guide →</Link>
                        </p>
                    </div>
                    <div className="md:col-span-5 flex flex-wrap gap-3">
                        {["USDC", "USDT", "BTC", "ETH", "XRP", "ALL"].map((p) => (
                            <div key={p} className="border border-ink-strong px-5 py-3 overline text-ink-dim">{p}</div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-32 border-t border-ink bg-ink-surface" data-testid="faq-section">
                <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12">
                    <div className="grid md:grid-cols-12 gap-8 items-start">
                        <div className="md:col-span-5">
                            <p className="section-num mb-4">/ 08 · FAQ</p>
                            <h2 className="font-display text-4xl sm:text-6xl font-bold text-ink tracking-tight leading-[0.95]">Questions,<br /><span className="text-neon">answered.</span></h2>
                            <p className="mt-6 text-ink-dim max-w-sm">Straight answers on shipping, testing, payment, and what "for research use only" really means.</p>
                        </div>
                        <div className="md:col-span-7 space-y-px bg-ink-strong">
                            {[
                                { q: "Is every product really third-party tested?", a: "Yes. Every lot of every product is assayed both in-house (domestic lab) AND by Janoshik analytical for purity (HPLC) and identity (mass spec). COA is linked on each product page." },
                                { q: "How discreet is the packaging?", a: "Plain brown mailer, no logos, no peptide names, no return address tied to research chemistry. Tracking is provided after dispatch." },
                                { q: "How fast does it ship?", a: "24 to 72 hours to leave our warehouse. Canadian orders: 3 to 5 business days. US orders: 5 to 10 business days cross-border." },
                                { q: "Do you accept crypto?", a: "Crypto is our ONLY payment method, we accept USDC (Solana), USDT (TRC20), BTC, ETH, and XRP via NowPayments. Prices stay in USD. Brand new to crypto? Our 3-minute guide at /how-to-pay-with-crypto walks you through setting up an exchange and paying for the first time." },
                                { q: "Is free shipping available?", a: "Orders over CA$200 ship free inside Canada. US orders ship flat $35 USD regardless of cart size." },
                                { q: "What does 'for research use only' mean?", a: "Our products are sold exclusively for in-vitro and laboratory research. They are not for human consumption, therapeutic use, or any in-vivo application. By ordering you confirm you're a qualified researcher." },
                            ].map((f, i) => (
                                <details key={i} className="group bg-ink-base p-5 cursor-pointer" data-testid={`faq-${i}`}>
                                    <summary className="flex items-center justify-between font-display text-lg font-bold text-ink list-none group-hover:text-neon transition-colors">
                                        <span>{f.q}</span>
                                        <span className="text-neon font-mono text-xl group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-4 text-sm text-ink-dim leading-relaxed">{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FULL GRID */}
            <section className="py-32 bg-ink-base" data-testid="all-section">
                <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12">
                    <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
                        <div className="md:col-span-8">
                            <p className="section-num mb-4">/ 06 · FULL INDEX</p>
                            <h2 className="font-display text-4xl sm:text-6xl font-bold text-ink tracking-tight leading-[0.95]">Every compound,<br /><span className="text-neon">tested.</span></h2>
                        </div>
                        <div className="md:col-span-4 flex md:justify-end">
                            <Link to="/shop" className="overline text-neon link-reveal inline-flex items-center gap-2">
                                OPEN SHOP <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {all.slice(0, 8).map((p, idx) => <ProductCard key={p.id} product={p} index={idx} />)}
                    </div>
                </div>
            </section>
        </div>
    );
}
